import React, { useRef, useEffect, useCallback } from 'react';
import * as Cesium from 'cesium';
import { useDrawingContext } from './DrawingContext';
import { createBoundingPolygon } from '../../utils/polygonUtils';
export const GeometryManager = React.memo(({ viewer, isActive, polygonEntityRef }) => {
    const { state, computed: { bufferedCoordinates }, actions } = useDrawingContext();
    const { geometry, ui } = state;
    const { area } = state;
    const { bufferDistanceMeters, showBufferConfig } = ui;
    // Buffer entity lives outside the drawing hook because it depends on
    // server-calculated buffered coordinates that arrive asynchronously and
    // are project-boundary-specific (the polygon annotation tool has no
    // equivalent concept).
    const bufferEntityRef = useRef(null);
    const bufferPointsRef = useRef([]);
    // Polygon colour override: the default polygon colour is crimson (matches
    // the polygon annotation tool). When the server reports area > tier max,
    // swap to a brighter alert orange-red so the validation-fail state stays
    // distinguishable from the crimson default. Pure RED was too close to
    // crimson to read as a state change.
    useEffect(() => {
        var _a, _b;
        if (!((_a = polygonEntityRef === null || polygonEntityRef === void 0 ? void 0 : polygonEntityRef.current) === null || _a === void 0 ? void 0 : _a.polygon))
            return;
        const msg = (((_b = area.data) === null || _b === void 0 ? void 0 : _b.message) || '').toLowerCase();
        const exceedsMax = !!(area.data && area.data.success === false && msg.includes('maximum area'));
        const fill = exceedsMax
            ? Cesium.Color.fromCssColorString('#FF4500').withAlpha(0.5)
            : Cesium.Color.fromCssColorString('#DC143C').withAlpha(0.3);
        const outline = exceedsMax
            ? Cesium.Color.fromCssColorString('#FF4500')
            : Cesium.Color.fromCssColorString('#DC143C');
        polygonEntityRef.current.polygon.material = new Cesium.ColorMaterialProperty(fill);
        polygonEntityRef.current.polygon.outlineColor = new Cesium.ConstantProperty(outline);
    }, [polygonEntityRef, area.data, geometry.points.length]);
    // Buffer polygon: the ring/donut between the original boundary and the
    // server-buffered coordinates. Recomputes whenever either changes or when
    // the buffer config flow is opened.
    const updateBufferPolygon = useCallback(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        if (bufferEntityRef.current) {
            viewer.entities.remove(bufferEntityRef.current);
            bufferEntityRef.current = null;
        }
        bufferPointsRef.current.forEach(entity => {
            viewer.entities.remove(entity);
        });
        bufferPointsRef.current = [];
        if (showBufferConfig && geometry.points.length >= 3 && bufferDistanceMeters > 0 && bufferedCoordinates.length > 0) {
            const orderedPoints = createBoundingPolygon(geometry.points);
            const bufferPositions = bufferedCoordinates.map(coord => Cesium.Cartesian3.fromDegrees(coord.lng, coord.lat, 0));
            if (bufferPositions.length >= 3) {
                bufferEntityRef.current = viewer.entities.add({
                    polygon: {
                        hierarchy: new Cesium.PolygonHierarchy(bufferPositions, [new Cesium.PolygonHierarchy(orderedPoints)]),
                        // Cyan reads as "water flow" and stays legible against the brown/green
                        // satellite terrain (a brown buffer camouflaged into it). A lighter
                        // fill + deeper outline keeps it distinct from the crimson AOI and,
                        // in DEM mode, from the #38BDF8 coverage outline drawn alongside it.
                        material: Cesium.Color.fromCssColorString('#38BDF8').withAlpha(0.25),
                        outline: true,
                        outlineColor: Cesium.Color.fromCssColorString('#0EA5E9').withAlpha(0.9),
                        outlineWidth: 1,
                        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                    }
                });
                actions.setBufferEntity(bufferEntityRef.current);
            }
        }
    }, [viewer, geometry.points, bufferDistanceMeters, showBufferConfig, bufferedCoordinates, actions]);
    useEffect(() => {
        updateBufferPolygon();
    }, [updateBufferPolygon]);
    // Cleanup buffer entity on unmount / deactivate.
    useEffect(() => {
        if (!isActive && viewer && !viewer.isDestroyed()) {
            if (bufferEntityRef.current) {
                viewer.entities.remove(bufferEntityRef.current);
                bufferEntityRef.current = null;
            }
            bufferPointsRef.current.forEach(entity => {
                if (!viewer.isDestroyed())
                    viewer.entities.remove(entity);
            });
            bufferPointsRef.current = [];
        }
    }, [isActive, viewer]);
    useEffect(() => {
        return () => {
            if (viewer && !viewer.isDestroyed()) {
                if (bufferEntityRef.current) {
                    viewer.entities.remove(bufferEntityRef.current);
                }
                bufferPointsRef.current.forEach(entity => {
                    if (!viewer.isDestroyed())
                        viewer.entities.remove(entity);
                });
            }
        };
    }, [viewer]);
    return null;
});
GeometryManager.displayName = 'GeometryManager';
